import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { Colors } from '@/constants/colors';
import { useComprehensiveAnalysis } from '@/hooks/useComprehensiveAnalysis';
import { useTickerSentiment } from '@/hooks/useTickerSentiment';
import { Ionicons } from '@expo/vector-icons';
import { CompanyOverviewCard } from './outlook/CompanyOverviewCard';
import { MarketStatusCard } from './outlook/MarketStatusCard';
import { FundamentalAnalysisCard } from './outlook/FundamentalAnalysisCard';
import { MarketSentimentCard } from './outlook/MarketSentimentCard';
import { RecentDevelopmentsCard } from './outlook/RecentDevelopmentsCard';
import { InvestmentOutlookCard } from './outlook/InvestmentOutlookCard';
import { RisksCatalystsCard } from './outlook/RisksCatalystsCard';

interface IntelligentOutlookTabProps {
  ticker: string;
  companyName: string;
  groupId?: string;
  onViewMessages?: () => void;
  includeAI?: boolean; // Whether to include AI messages in sentiment calculation
}

export const IntelligentOutlookTab: React.FC<IntelligentOutlookTabProps> = ({
  ticker,
  companyName,
  groupId,
  onViewMessages,
  includeAI = true,
}) => {
  const { analysis, isLoading, error, cacheAge } = useComprehensiveAnalysis(ticker, companyName);
  const { sentiment, isLoading: sentimentLoading } = useTickerSentiment(
    groupId,
    ticker,
    '7d',
    includeAI
  );

  // Error state
  if (error && !isLoading) {
    return (
      <View style={styles.errorContainer}>
        <Ionicons name="alert-circle-outline" size={64} color={Colors.status.error} />
        <Text style={styles.errorTitle}>Failed to load analysis</Text>
        <Text style={styles.errorText}>{error}</Text>
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.contentContainer}>
      {/* Group Sentiment Card (if groupId provided) */}
      {groupId && sentiment && sentiment.totalMentions > 0 && (
        <View style={styles.card}>
          <View style={styles.cardHeader}>
            <Ionicons name="people" size={20} color={Colors.ai.primary} />
            <Text style={styles.cardTitle}>Group Sentiment</Text>
          </View>

          <View style={styles.sentimentRow}>
            <View style={[styles.sentimentBar, { flex: sentiment.bullishCount }]}>
              <View style={[styles.sentimentFill, { backgroundColor: '#34C759' }]} />
            </View>
            <View style={[styles.sentimentBar, { flex: sentiment.bearishCount || 0.1 }]}>
              <View style={[styles.sentimentFill, { backgroundColor: '#FF3B30' }]} />
            </View>
          </View>

          <View style={styles.sentimentStats}>
            <View style={styles.sentimentStat}>
              <Text style={[styles.sentimentPercent, { color: '#34C759' }]}>
                {Math.round((sentiment.bullishCount / sentiment.totalMentions) * 100)}%
              </Text>
              <Text style={styles.sentimentLabel}>Bullish</Text>
            </View>
            <View style={styles.sentimentStat}>
              <Text style={styles.sentimentMentions}>
                {sentiment.totalMentions} {sentiment.totalMentions === 1 ? 'mention' : 'mentions'}
              </Text>
              <Text style={styles.sentimentTimeframe}>Last 7 days</Text>
            </View>
            <View style={styles.sentimentStat}>
              <Text style={[styles.sentimentPercent, { color: '#FF3B30' }]}>
                {Math.round((sentiment.bearishCount / sentiment.totalMentions) * 100)}%
              </Text>
              <Text style={styles.sentimentLabel}>Bearish</Text>
            </View>
          </View>

          {onViewMessages && (
            <TouchableOpacity style={styles.viewMessagesButton} onPress={onViewMessages}>
              <Text style={styles.viewMessagesText}>View Messages</Text>
              <Ionicons name="chevron-forward" size={16} color={Colors.ai.primary} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Modular Cards - Show skeleton while loading, fill with data when available */}
      <CompanyOverviewCard data={analysis?.companyOverview} isLoading={isLoading} />
      <MarketStatusCard data={analysis?.marketStatus} isLoading={isLoading} />
      <FundamentalAnalysisCard data={analysis?.fundamentalAnalysis} isLoading={isLoading} />
      <MarketSentimentCard data={analysis?.marketSentiment} isLoading={isLoading} />
      <RecentDevelopmentsCard data={analysis?.recentDevelopments} isLoading={isLoading} />
      <InvestmentOutlookCard data={analysis?.investmentOutlook} isLoading={isLoading} />
      <RisksCatalystsCard data={analysis?.risksAndCatalysts} isLoading={isLoading} />

      {/* Footer */}
      {!isLoading && analysis && (
        <View style={styles.footer}>
          <Text style={styles.footerText}>Generated by Perplexity AI</Text>
          <Text style={styles.footerText}>•</Text>
          <Text style={styles.footerText}>Updated {cacheAge || '0h'} ago</Text>
        </View>
      )}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.background.primary,
  },
  contentContainer: {
    padding: 16,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: Colors.background.primary,
    paddingHorizontal: 32,
  },
  errorTitle: {
    marginTop: 16,
    fontSize: 18,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  errorText: {
    marginTop: 8,
    fontSize: 14,
    color: Colors.text.secondary,
    textAlign: 'center',
  },
  card: {
    backgroundColor: Colors.background.secondary,
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: Colors.text.primary,
    flex: 1,
  },
  sentimentRow: {
    flexDirection: 'row',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    marginBottom: 12,
  },
  sentimentBar: {
    height: '100%',
  },
  sentimentFill: {
    width: '100%',
    height: '100%',
  },
  sentimentStats: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sentimentStat: {
    alignItems: 'center',
  },
  sentimentPercent: {
    fontSize: 18,
    fontWeight: '700',
  },
  sentimentLabel: {
    fontSize: 12,
    color: Colors.text.secondary,
    marginTop: 2,
  },
  sentimentMentions: {
    fontSize: 16,
    fontWeight: '600',
    color: Colors.text.primary,
  },
  sentimentTimeframe: {
    fontSize: 12,
    color: Colors.text.secondary,
    marginTop: 2,
  },
  viewMessagesButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 12,
    paddingVertical: 8,
    gap: 4,
  },
  viewMessagesText: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.ai.primary,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 16,
  },
  footerText: {
    fontSize: 12,
    color: Colors.text.tertiary,
  },
});
